import { Button, InputOTP, REGEXP_ONLY_DIGITS } from "@heroui/react";
import { memo, useState } from "react";
import { StepHeading } from "@funnel-ui/primitives/StepHeading";

type OtpVerificationProps = {
  question?: string;
  description?: string;
  eyebrow?: string;
  ctaLabel?: string;
  length?: number;
  isSubmitting?: boolean;
  errorMessage?: string;
  onSubmitOtp?: (code: string) => void;
  className?: string;
};

const OtpVerificationImpl = ({
  question = "Enter verification code",
  description = "We sent a 6-digit code to your email.",
  eyebrow,
  ctaLabel = "Verify",
  length = 6,
  isSubmitting = false,
  errorMessage,
  onSubmitOtp,
  className,
}: OtpVerificationProps) => {
  const [code, setCode] = useState("");

  const handleComplete = (value: string) => {
    setCode(value);
    onSubmitOtp?.(value);
  };

  const handleSubmit = () => {
    if (code.length === length && onSubmitOtp) {
      onSubmitOtp(code);
    }
  };

  return (
    <section className={`flex flex-col gap-5 ${className ?? ""}`}>
      <StepHeading title={question} description={description} eyebrow={eyebrow} />

      <div className="flex flex-col items-center gap-4">
        <InputOTP
          maxLength={length}
          pattern={REGEXP_ONLY_DIGITS}
          value={code}
          onChange={setCode}
          onComplete={handleComplete}
          isInvalid={!!errorMessage}
          isDisabled={isSubmitting}
        >
          <InputOTP.Group>
            {Array.from({ length }, (_, i) => (
              <InputOTP.Slot
                key={i}
                index={i}
                className="border border-[hsl(var(--hero-border))] bg-[hsl(var(--hero-surface-2))] text-[hsl(var(--hero-fg))]"
              />
            ))}
          </InputOTP.Group>
        </InputOTP>

        {errorMessage && (
          <p className="text-sm" style={{ color: "hsl(var(--hero-danger))" }}>
            {errorMessage}
          </p>
        )}
      </div>

      <Button
        variant="primary"
        size="lg"
        fullWidth
        onPress={handleSubmit}
        isDisabled={code.length < length || isSubmitting}
        className="rounded-full"
      >
        {ctaLabel}
      </Button>
    </section>
  );
};

export const OtpVerification = memo(OtpVerificationImpl);

export default OtpVerification;
